// Inspecteur register shared kit (FRONTEND-BLUEPRINT §3.1 + §4). Re-skins the read-register LIST screens
// on the shadcn design system: every register composes PageHeader + DataTable + a shadcn Dialog create/edit
// form, reusing the EXISTING api-client methods (no new endpoints). Frontend-only.
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import type { PermissionCode } from "@sympllex/auth";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export type Row = Record<string, unknown>;
export const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);

/** Map a status/lifecycle token to a stable shadcn Badge variant (deterministic so a given value always
 *  renders the same colour across screens). */
type BadgeVariant = "default" | "secondary" | "destructive" | "outline";
const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  // verwerking / generic lifecycle
  design: "secondary", pbd_review: "outline", validated: "default", active: "default",
  under_review: "outline", archived: "secondary",
  // transfer TIA lifecycle
  draft: "secondary", in_review: "outline", approved: "default", superseded: "secondary",
};

export function StatusBadge({ value }: { value: unknown }) {
  if (value == null || value === "") return <span className="text-muted-foreground">—</span>;
  const token = String(value);
  const variant = STATUS_VARIANTS[token] ?? "outline";
  return <Badge variant={variant} className="capitalize">{token.replace(/_/g, " ")}</Badge>;
}

/** A category/class/kind tag rendered as a neutral secondary badge. */
export function TagBadge({ value }: { value: unknown }) {
  if (value == null || value === "") return <span className="text-muted-foreground">—</span>;
  return <Badge variant="secondary">{String(value)}</Badge>;
}

/** Truncate long free-text for a table cell, keeping the full text in a title tooltip. */
export function truncate(value: unknown, max = 80): ReactNode {
  if (value == null || value === "") return <span className="text-muted-foreground">—</span>;
  const s = String(value);
  return <span title={s.length > max ? s : undefined}>{s.length > max ? `${s.slice(0, max)}…` : s}</span>;
}

export interface FormField {
  key: string;
  label: string;
  type?: "text" | "textarea" | "select";
  required?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
}

/** A field row used inside the create/edit Dialog (shadcn Input/Textarea, native styled select). */
function Field({ field, value, onChange }: { field: FormField; value: string; onChange: (v: string) => void }) {
  const { t } = useI18n();
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={`f-${field.key}`}>{field.label}{field.required ? " *" : ""}</Label>
      {field.type === "textarea" ? (
        <Textarea id={`f-${field.key}`} value={value} rows={3} onChange={(e) => onChange(e.target.value)} {...(field.placeholder ? { placeholder: field.placeholder } : {})} />
      ) : field.type === "select" ? (
        <select
          id={`f-${field.key}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-full rounded-2xl border border-transparent bg-input/50 px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
        >
          <option value="">{t("crud.select")}</option>
          {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <Input id={`f-${field.key}`} value={value} onChange={(e) => onChange(e.target.value)} {...(field.placeholder ? { placeholder: field.placeholder } : {})} />
      )}
    </div>
  );
}

const missingRequired = (fields: FormField[], read: (k: string) => string) =>
  fields.some((f) => f.required && !read(f.key).trim());

/** Create dialog — builds the input map from non-empty fields and calls `onCreate`. exactOptionalPropertyTypes
 *  safe: only non-empty values are spread into the payload. */
export function CreateDialog({
  title, fields, initial, onCreate, onDone, note, disabled,
}: {
  title: string;
  fields: FormField[];
  initial?: Record<string, string>;
  onCreate: (input: Record<string, unknown>) => Promise<unknown>;
  onDone: () => void;
  note?: ReactNode;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<Record<string, string>>(initial ?? {});
  const set = (k: string, v: string) => setState((s) => ({ ...s, [k]: v }));
  const reset = () => setState(initial ?? {});
  const mut = useMutation({
    mutationFn: () => {
      const out: Record<string, unknown> = {};
      for (const f of fields) { const v = state[f.key]?.trim(); if (v) out[f.key] = v; }
      return onCreate(out);
    },
    onSuccess: () => { reset(); setOpen(false); onDone(); },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { reset(); mut.reset(); } }}>
      <DialogTrigger asChild>
        <Button size="sm" disabled={disabled}>{t("crud.new")}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {note ? <DialogDescription>{note}</DialogDescription> : null}
        </DialogHeader>
        <div className="grid gap-3">
          {fields.map((f) => <Field key={f.key} field={f} value={state[f.key] ?? ""} onChange={(v) => set(f.key, v)} />)}
          {mut.isError && <p className="text-sm text-destructive">{errText(mut.error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); reset(); mut.reset(); }}>{t("common.cancel")}</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || missingRequired(fields, (k) => state[k] ?? "")}>
            {mut.isPending ? t("crud.creating") : t("common.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Edit dialog over the generic getEntity/updateEntity client methods, keyed on a TENANT_PATHS basePath. */
export function EditDialog({
  basePath, id, title, fields, onClose, onDone,
}: {
  basePath: string;
  id: string;
  title: string;
  fields: FormField[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const { data, isLoading, error } = useQuery({ queryKey: [basePath, id], queryFn: () => api.getEntity(basePath, id) });
  const row = (data ?? null) as Row | null;
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const val = (k: string) => overrides[k] ?? (row?.[k] != null ? String(row[k]) : "");
  const set = (k: string, v: string) => setOverrides((s) => ({ ...s, [k]: v }));
  const save = useMutation({
    mutationFn: () => api.updateEntity(basePath, id, Object.fromEntries(fields.map((f) => [f.key, val(f.key)]))),
    onSuccess: onDone,
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>id {id}</DialogDescription>
        </DialogHeader>
        {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
        {error && <p className="text-sm text-destructive">{errText(error)}</p>}
        {row && (
          <div className="grid gap-3">
            {fields.map((f) => <Field key={f.key} field={f} value={val(f.key)} onChange={(v) => set(f.key, v)} />)}
            {save.isError && <p className="text-sm text-destructive">{errText(save.error)}</p>}
            {save.isSuccess && <p className="text-sm text-muted-foreground">{t("crud.saved")}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("crud.close")}</Button>
          {row && (
            <Button onClick={() => save.mutate()} disabled={save.isPending || missingRequired(fields, val)}>
              {save.isPending ? t("crud.saving") : t("crud.saveChanges")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Deactivate (soft-delete) confirm button for a register row, over deactivateEntity. */
export function DeactivateButton({ basePath, id, onDone }: { basePath: string; id: string; onDone: () => void }) {
  const { t } = useI18n();
  const [confirm, setConfirm] = useState(false);
  const mut = useMutation({ mutationFn: () => api.deactivateEntity(basePath, id), onSuccess: () => { setConfirm(false); onDone(); } });
  if (!confirm) return <Button variant="ghost" size="xs" className="text-destructive" onClick={() => setConfirm(true)}>{t("crud.deactivate")}</Button>;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      {t("crud.sure")}
      <Button variant="ghost" size="xs" className="text-destructive" disabled={mut.isPending} onClick={() => mut.mutate()}>{t("crud.yes")}</Button>
      <Button variant="ghost" size="xs" onClick={() => setConfirm(false)}>{t("crud.no")}</Button>
    </span>
  );
}

/** The generic re-skinned register LIST screen: PageHeader + a client-side search box + DataTable. */
export function RegisterScreen({
  title, description, queryKey, listFn, columns, empty, readPerm, actions, rowActions, getRowId, help, onRowClick,
}: {
  title: string;
  description?: string;
  queryKey: string;
  listFn: () => Promise<unknown[]>;
  columns: Column<Row>[];
  empty: ReactNode;
  readPerm: PermissionCode;
  actions?: ReactNode;
  rowActions?: (row: Row) => ReactNode;
  getRowId?: (row: Row) => string;
  help?: string; // P3 — optional contextual-help "?" passed to PageHeader
  onRowClick?: (row: Row) => void; // drill-in to a detail page (design-vs-build gaps)
}) {
  const { t } = useI18n();
  const { data, isLoading, error } = useQuery({ queryKey: [queryKey], queryFn: listFn });
  const allRows = useMemo(() => (data ?? []) as Row[], [data]);
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const rows = useMemo(
    () => (needle ? allRows.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(needle))) : allRows),
    [allRows, needle],
  );
  const idOf = getRowId ?? ((r: Row) => String(r.id ?? ""));

  return (
    <section>
      <Can perm={readPerm} fallback={<p className="text-sm text-muted-foreground">{t("crud.readonly")}</p>}>
        <PageHeader title={title} {...(description ? { description } : {})} {...(actions ? { actions } : {})} {...(help ? { help } : {})} />
        {allRows.length > 0 && (
          <div className="mb-3 max-w-sm">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("crud.search")} aria-label={t("crud.search")} />
          </div>
        )}
        {error ? (
          <p className="text-sm text-destructive">{t("crud.failed")} {errText(error)}</p>
        ) : (
          <DataTable<Row>
            columns={columns}
            rows={rows}
            loading={isLoading}
            empty={needle ? `${t("crud.noMatches")} “${q}”.` : empty}
            getRowId={idOf}
            {...(rowActions ? { rowActions } : {})}
            {...(onRowClick ? { onRowClick } : {})}
          />
        )}
      </Can>
    </section>
  );
}
